import * as QUESTION_TYPES from '../constants/question_types';

// Converts the answer format returned by the `employeeSurvey` endpoint
// to the internal app format.
//
// Example employeeSurvey answer:
// ```
// {
//   "questionId": 123,
//   "questionType": 'OPEN',
//   "comment": "this is an optional comment",
//   "rating": null,
//   "skippedQuestion": false,
//   "answerText": "this is my answer",
//   "selectedOptions": null
// }
// ```
//
// Example app answer:
// ```
// ["123"]: {
//   value: "this is my answer",
//   comment: "this is an optional comment",
//   isSkipped: false
// }
// ```
const normalizedAnswers = (rawAnswers) => {
  const answers = rawAnswers.reduce((transformedAnswers, rawAnswer) => {
    // If not a valid question type, skip answer.
    if (!QUESTION_TYPES.LIST.includes(rawAnswer.questionType)) {
      return transformedAnswers;
    }

    // Start construction of answer object with properties common to all question types.
    const answer = {
      questionType: rawAnswer.questionType,
      comment: rawAnswer.comment,
      isSkipped: rawAnswer.skippedQuestion,
      isSubmitted: true, // assumes answers picked up from server are considered "submitted"
    };

    // The server endpoint `employeeSurvey` returns 3 types of values for:
    // 1. scale/rating
    // 2. text
    // 3. multiple choice
    switch (rawAnswer.questionType) {
      case QUESTION_TYPES.RATING:
      case QUESTION_TYPES.ENPS:
      case QUESTION_TYPES.ICONS:
      case QUESTION_TYPES.YES_NO:
        answer.value = rawAnswer.rating;
        break;
      case QUESTION_TYPES.MULTIPLE_CHOICE:
      case QUESTION_TYPES.DROPDOWN:
        answer.value = rawAnswer.selectedOptions;
        break;
      case QUESTION_TYPES.TEXT:
      default:
        answer.value = rawAnswer.answerText;
    }

    return {
      ...transformedAnswers,
      [rawAnswer.questionId]: answer,
    };
  }, {});

  return answers;
};

const hasAnsweredValue = (answer) => {
  if (!answer) return false;

  if (Array.isArray(answer.value)) {
    return answer.value.length > 0;
  }
  return !!answer.value || answer.value === 0;
};

// Whitespace-only text is blank: the API rejects it for a required question.
const isAnswered = (question, answer) => {
  if (!hasAnsweredValue(answer)) return false;
  return !(question.type === QUESTION_TYPES.TEXT && typeof answer.value === 'string' && answer.value.trim() === '');
};

const isValidAnswer = (question, answer) => {
  const hasAnswer = isAnswered(question, answer);
  const hasMinRequiredSelections =
    hasAnswer &&
    (!question.minRequiredSelections ||
      (Array.isArray(answer.value) && answer.value.length >= question.minRequiredSelections));
  const hasNoMoreThanMaxRequiredSelections =
    hasAnswer &&
    (!question.maxRequiredSelections ||
      (Array.isArray(answer.value) && answer.value.length <= question.maxRequiredSelections));

  // If question is NOT required, answer is "valid" even if it has no value.
  const hasValidRequirements = question.isRequired
    ? hasMinRequiredSelections && hasNoMoreThanMaxRequiredSelections
    : true;

  return hasValidRequirements;
};

export { normalizedAnswers, isValidAnswer, hasAnsweredValue, isAnswered };
